import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Flex } from 'nallian-shared-ui/lib/components';
import { ImpersonationRequestDetailView, ImpersonationStatus } from 'nallian-shared-ui/lib/impersonation';
import impersonationRequestDetailService from '../services/impersonationRequestDetailService';
import revokeImpersonationService from '../services/revokeImpersonationService';

const ImpersonationRequestDetail = ({
  open,
  requestId,
  onClose,
  dispatchImpersonationRequestDetailGet,
  impersonationRequestDetailFetch,
  dispatchRevokeImpersonationPut,
  revokeImpersonationFetch,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (open && requestId) {
      dispatchImpersonationRequestDetailGet(requestId);
    }
  }, [open, requestId, dispatchImpersonationRequestDetailGet]);

  const handleTerminate = useCallback(() => {
    dispatchRevokeImpersonationPut(requestId, onClose, t);
  }, [requestId, dispatchRevokeImpersonationPut, onClose, t]);

  if (!open) return null;

  const request = impersonationRequestDetailFetch?.value;
  const isProcessing =
    impersonationRequestDetailFetch?.pending || revokeImpersonationFetch?.pending;
  const canTerminate = request?.availableActions?.includes('Revoke');

  return (
    <Modal open closeIcon onClose={onClose} size="small">
      <Modal.Header>
        <Flex justifyContent="space-between" alignItems="center">
          {t('impersonationRequestDetails')}
          <ImpersonationStatus value={request?.status} />
        </Flex>
      </Modal.Header>
      <Modal.Content>
        <ImpersonationRequestDetailView
          request={request}
          loading={isProcessing}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button content={t('close')} onClick={onClose} />
        {canTerminate && (
          <Button
            content={t('terminateAccess')}
            color="red"
            onClick={handleTerminate}
            disabled={isProcessing}
          />
        )}
      </Modal.Actions>
    </Modal>
  );
};

ImpersonationRequestDetail.propTypes = {
  open: PropTypes.bool.isRequired,
  requestId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  dispatchImpersonationRequestDetailGet: PropTypes.func.isRequired,
  impersonationRequestDetailFetch: PropTypes.object.isRequired,
  dispatchRevokeImpersonationPut: PropTypes.func.isRequired,
  revokeImpersonationFetch: PropTypes.object.isRequired,
};

export default compose(impersonationRequestDetailService, revokeImpersonationService)(
  ImpersonationRequestDetail
);
